import { EMPTY_ARRAY, useSecuritiesContext } from '@talos/kyoko';
import { kebabCase } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { getVisibleConditionalDefaultLayoutSpecs } from '../components/AppLayout/Layouts/conditionalLayoutSpecs';
import { useAppLayoutConfig } from '../providers/AppLayoutConfig/AppLayoutConfigContext';
import type { NavigationItem } from '../tokens/navigationPaths';
import type { LayoutSection, LayoutSpec } from '../types/LayoutConfig';
import { useDefaultSpaces } from './useDefaultSpaces';
import { useFeatureFlag } from './useFeatureFlag';
import { useRoleAuth } from './useRoleAuth';

export function toNavGroupName(groupName: string): string {
  const trimmed = groupName.trim();
  return kebabCase(trimmed);
}

function groupLabelKey(groupName: string | undefined): string {
  return groupName == null || groupName === '' ? '__ungrouped__' : kebabCase(groupName.trim());
}

function groupSlugFromPath(path: string | undefined, layoutSection: LayoutSection): string | undefined {
  const sectionPath = `/${layoutSection}`;
  if (path == null || path === sectionPath) {
    return undefined;
  }
  return path.startsWith(`${sectionPath}/`) ? path.slice(sectionPath.length + 1).split('/')[0] : undefined;
}

function orderGroupsForNavigation(items: NavigationItem[]): NavigationItem[] {
  const unlabeledGroups = items.filter(item => item.label == null || item.label === '');
  const labeledGroups = items.filter(item => item.label != null && item.label !== '');
  return [...unlabeledGroups, ...labeledGroups];
}

export function mergeSpacesNavItems(
  layoutSection: LayoutSection,
  layoutConfigs: readonly LayoutSpec[],
  fixedNavItems: readonly NavigationItem[] = []
): NavigationItem[] {
  const layoutsByGroup = new Map<string, LayoutSpec[]>();
  for (const layout of layoutConfigs) {
    const key = groupLabelKey(layout.group);
    const groupLayouts = layoutsByGroup.get(key) ?? [];
    groupLayouts.push(layout);
    layoutsByGroup.set(key, groupLayouts);
  }

  const mergedFixedItems = fixedNavItems.map(fixedItem => {
    const key = groupLabelKey(fixedItem.label);
    const groupLayouts = layoutsByGroup.get(key) ?? [];
    layoutsByGroup.delete(key);

    if (groupLayouts.length === 0) {
      return { ...fixedItem, allowSpaceDrops: true } as NavigationItem;
    }

    const navGroupName = groupSlugFromPath(fixedItem.path, layoutSection);
    return {
      ...fixedItem,
      // A fixed group's visibility must not hide spaces that share it; fixed children retain their own visibility.
      isVisible: undefined,
      allowSpaceDrops: true,
      children: [
        ...(fixedItem.children ?? []),
        ...groupLayouts.map(layout => toSpaceNavItem(layout, layoutSection, navGroupName)),
      ],
    } as NavigationItem;
  });

  const dynamicItems = [...layoutsByGroup.values()].map(groupLayouts => {
    const groupName = groupLayouts[0]?.group;
    const navGroupName = groupName ? toNavGroupName(groupName) : undefined;
    return {
      path: navGroupName ? `/${layoutSection}/${navGroupName}` : `/${layoutSection}`,
      label: groupName,
      allowSpaceDrops: true,
      children: groupLayouts.map(layout => toSpaceNavItem(layout, layoutSection, navGroupName)),
    } satisfies NavigationItem;
  });

  return orderGroupsForNavigation([...mergedFixedItems, ...dynamicItems]);
}

export function useSpacesNavItems(
  layoutSection: LayoutSection,
  fixedNavItems: readonly NavigationItem[] = EMPTY_ARRAY
): NavigationItem[] | undefined {
  const { layoutConfigs, spacesConfigs, initializeSpaces, updateSpacesConfigs } = useAppLayoutConfig(layoutSection);
  const { initialized } = spacesConfigs;
  const defaultSpacesToAdd = useDefaultSpaces(layoutSection, 'all');
  const newUserSpacesToAdd = useDefaultSpaces(layoutSection, 'new-users');
  const flags = useFeatureFlag();
  const { isAuthorized } = useRoleAuth();
  const securities = useSecuritiesContext();

  // Add default spaces to the layout configs stored in app config
  useEffect(() => {
    if (layoutConfigs == null || defaultSpacesToAdd == null || newUserSpacesToAdd == null || !flags.isLoaded) {
      return;
    }

    const existingIds = new Set(layoutConfigs.map(l => l.id));
    const toAdd: typeof layoutConfigs = [];

    for (const spec of defaultSpacesToAdd) {
      if (!existingIds.has(spec.Config.layoutSpec.id)) {
        toAdd.push({ ...spec.Config.layoutSpec, deviceType: undefined, isRemovable: true });
        existingIds.add(spec.Config.layoutSpec.id);
      }
    }

    // If the spaces are not initialized, add the new user spaces to the layout configs
    if (!initialized) {
      for (const spec of newUserSpacesToAdd) {
        if (!existingIds.has(spec.Config.layoutSpec.id)) {
          toAdd.push({ ...spec.Config.layoutSpec, deviceType: undefined, isRemovable: true });
          existingIds.add(spec.Config.layoutSpec.id);
        }
      }

      for (const layoutSpec of getVisibleConditionalDefaultLayoutSpecs(layoutSection, {
        flags,
        isAuthorized,
        securities,
      })) {
        if (!existingIds.has(layoutSpec.id)) {
          toAdd.push({ ...layoutSpec, isRemovable: true });
          existingIds.add(layoutSpec.id);
        }
      }
    }

    // Only update the layout configs if there are new spaces
    if (toAdd.length > 0) {
      updateSpacesConfigs([...layoutConfigs, ...toAdd]);
    }

    // Mark spaces as initialized after the first run to prevent re-adding removed spaces on subsequent runs
    if (!initialized) {
      initializeSpaces();
    }
  }, [
    initialized,
    layoutConfigs,
    defaultSpacesToAdd,
    newUserSpacesToAdd,
    updateSpacesConfigs,
    initializeSpaces,
    layoutSection,
    flags,
    isAuthorized,
    securities,
  ]);

  const spacesNavItems = useMemo(() => {
    if (!layoutConfigs) {
      return undefined;
    }

    return mergeSpacesNavItems(layoutSection, layoutConfigs, fixedNavItems);
  }, [fixedNavItems, layoutConfigs, layoutSection]);

  return spacesNavItems;
}

function toSpaceNavItem(layout: LayoutSpec, layoutSection: LayoutSection, navGroupName?: string): NavigationItem {
  const basePath = navGroupName ? `/${layoutSection}/${navGroupName}/${layout.id}` : `/${layoutSection}/${layout.id}`;

  return {
    id: layout.id,
    path: basePath,
    label: layout.label,
    isEditable: true,
    isRemovable: layout.isRemovable ?? true,
    deviceType: layout.deviceType,
    children: [
      {
        path: `${basePath}/*`,
        label: undefined,
      },
    ],
  };
}
